import React from 'react';
import { useSelector } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import Switch from 'react-switch';
import { format, differenceInCalendarDays, e4Format, e4Formats } from 'helpers/dates';

import { DeleteWarning, DomHelpers, FloatingMenu, Forms, Loading, Paging, SystemText } from '@salesperformanceint/e4shared';

import BlobImage from 'components/blobImage';
import PageHeader from 'components/general/pageHeader';
import NoResults from 'components/general/noResults';
import SortFilter from 'components/general/sortFilter';
import UserMenu from 'components/user/menu';
import ManagePost from './components/managePost';

import { apiGet, apiPatch, apiDelete } from 'services';
import { hasModule } from 'helpers/profile';

//import '../training.css';

export const Posts = ({ history, match }) => {
    const defaultValues = { published: false, date: new Date() };

    const didMountRef = React.useRef(false);
    const user = useSelector(state => state.user.user);
    const leftAsideOpen = useSelector(state => state.ui.leftAsideOpen);

    const [loading, setLoading] = React.useState(true);
    const [postsLoading, setPostsLoading] = React.useState(true);
    const [posts, setPosts] = React.useState([]);
    const [profileLoading, setProfileLoading] = React.useState(false);
    const [profile, setProfile] = React.useState(null);
    const [competencies, setCompetencies] = React.useState([]);
    const [recordCount, setRecordCount] = React.useState(0);
    const [hasInactivePosts, setHasInactivePosts] = React.useState(false);
    const [showInactivePosts, setShowInactivePosts] = React.useState(false);
    const [showManagePost, setShowManagePost] = React.useState(false);
    const [showWarning, setShowWarning] = React.useState(false);
    const [post, setPost] = React.useState({ ...defaultValues });
    const [page, setPage] = React.useState(1);
    const [search, setSearch] = React.useState('');
    const [sort, setSort] = React.useState('date');
    const [ascending, setAsending] = React.useState(false);
    const [competency, setCompetency] = React.useState('');
    const [profileId, setProfileId] = React.useState(parseInt(match.params.id, 10));

    if (!hasModule(user, 'blogs')) history.replace('/');

    const pageSize = 12;

    const resizeHandler = () => {
        setTimeout(() => {
            DomHelpers.arrangeTiles('box-blog');
        }, 500);
    };

    React.useEffect(() => {
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, []); // empty array => run only once

    React.useEffect(() => resizeHandler, [leftAsideOpen]);

    React.useEffect(() => {
        if (didMountRef.current) {
            resizeHandler();
        }
    }, [didMountRef]);

    const loadPosts = () => {
        setPostsLoading(true);
        apiGet(
            `all-blogs?id=${profileId || 0}&page=${page || 1}&search=${
                search || ''
            }&sort=${sort}&asc=${ascending}&competency=${competency || 0}&inactive=${showInactivePosts}`,
            user.guid,
            response => {
                setPosts(response.posts);
                setRecordCount(response.records);
                setHasInactivePosts(response.hasInactive);
                setPostsLoading(false);
                setTimeout(resizeHandler, 100);
            }
        );
    };

    const loadCompetences = () => {
        apiGet(`competency`, user.guid, response => {
            setCompetencies(response.map(c => ({ name: `${c.area} - ${c.name}`, id: c.id })));
        });
    };

    const loadProfile = () => {
        setProfileLoading(true);
        apiGet(
            `profile/${profileId || user.id}`,
            user.guid,
            response => {
                setProfile(response);
                setProfileLoading(false);
            },
            () => {
                setProfileId(null);
                setProfileLoading(false);
            }
        );
    };

    const togglePost = post => {
        apiPatch('blog/post', { ...post, published: !post.published }, user.guid, loadPosts);
    };

    const deletePost = confirm => {
        const id = post.id;
        setPost({});

        setShowWarning(false);
        if (confirm) {
            apiDelete(`post/${id}`, null, user.guid, () => {
                loadPosts();
            });
        }
    };

    React.useEffect(() => {
        if (user?.guid) {
            loadCompetences();
        }
    }, [user.guid]);

    React.useEffect(() => {
        if (user?.guid) {
            loadPosts();
            loadProfile();
        }
    }, [user.guid, profileId, search, sort, ascending, competency, page, showInactivePosts]);

    React.useEffect(() => {
        setPage(1);
    }, [search, sort, ascending, competency, showInactivePosts]);

    React.useEffect(() => {
        setLoading(postsLoading || profileLoading);
    }, [postsLoading, profileLoading]);

    const text = {
        title: SystemText('Blog/Title'),
        new: SystemText('Blog/New'),
        by: SystemText('Blog/By'),
        notLive: SystemText('Blog/NotLive'),
        warning: SystemText('Blog/Warning'),
        buttons: {
            add: SystemText('Blog/Buttons/Add'),
            include: SystemText('Blog/Buttons/Include'),
            latest: SystemText('Blog/Buttons/Latest'),
            oldest: SystemText('Blog/Buttons/Oldest'),
            title: SystemText('Blog/Buttons/Title'),
            popularity: SystemText('Blog/Buttons/Popularity'),
            yourBlog: SystemText('Blog/Buttons/YourBlog'),
            allBlogs: SystemText('Blog/Buttons/AllBlogs'),
            delete: SystemText('Blog/Buttons/Delete'),
            edit: SystemText('Blog/Buttons/Edit'),
            publish: SystemText('Blog/Buttons/Publish'),
            unpublish: SystemText('Blog/Buttons/Unpublish'),
        },
        fields: {
            filter: {
                competency: SystemText('Blog/Fields/Competency'),
                search: SystemText('Blog/Fields/Search'),
            },
        },
        noResults: {
            title: SystemText('Blog/NoResults/Title'),
            message: SystemText('Blog/NoResults/Message'),
        },
        noPost: {
            title: SystemText('Blog/NoPost/Title'),
            message: SystemText('Blog/NoPost/Message'),
            start: SystemText('Blog/NoPost/Start'),
        },
        noPosts: {
            title: SystemText('Blog/NoPosts/Title'),
            message: SystemText('Blog/NoPosts/Message'),
        },
    };

    const toggle = () =>
        hasInactivePosts ? (
            <label className={showInactivePosts ? 'switch-on' : 'switch-off'}>
                <span>{text.buttons.include}</span>
                <Switch
                    className="react-switch"
                    onChange={() => setShowInactivePosts(!showInactivePosts)}
                    checked={showInactivePosts}
                />
            </label>
        ) : null;

    const addNewButton = () => (
        <div className="flex">
            <button
                className="btn btn-success"
                id="add-new-post-button"
                onClick={() => {
                    setPost({});
                    setShowManagePost(true);
                }}
            >
                <i className="fa fa-plus mr10" />
                {text.buttons.add}
            </button>
            <UserMenu profile={profile} user={user} hide={'blogs'} />
        </div>
    );

    const filterFields = () => (
        <div className="row">
            <div className="col-md-6">
                <Forms.Text
                    id="search"
                    label={text.fields.filter.search}
                    value={search}
                    onChange={setSearch}
                    icon="fa-search"
                />
            </div>
            <div className="col-md-6">
                <Forms.Select
                    id="competency_Id"
                    label={text.fields.filter.competency}
                    data={competencies}
                    value={competency}
                    onChange={setCompetency}
                />
            </div>
        </div>
    );

    const sortButtons = () => (
        <div className="flex flex-center">
            <div className="btn-row flex">
                <button
                    onClick={() => {
                        setSort('date');
                        setAsending(false);
                    }}
                    className={`btn btn-sm btn-${sort === 'date' && !ascending ? 'primary' : 'default'}`}
                >
                    {text.buttons.latest}
                </button>
                <button
                    onClick={() => {
                        setSort('date');
                        setAsending(true);
                    }}
                    className={`btn btn-sm btn-${sort === 'date' && ascending ? 'primary' : 'default'}`}
                >
                    {text.buttons.oldest}
                </button>
                <button
                    onClick={() => {
                        setSort('title');
                        setAsending(true);
                    }}
                    className={`btn btn-sm btn-${sort === 'title' ? 'primary' : 'default'}`}
                >
                    {text.buttons.title}
                </button>
                <button
                    onClick={() => {
                        setSort('likes');
                        setAsending(false);
                    }}
                    className={`btn btn-sm btn-${sort === 'likes' ? 'primary' : 'default'}`}
                >
                    {text.buttons.popularity}
                </button>
            </div>
            <div className="ml10 text-sm">
                {profileId ? (
                    <Link className="underline-hover" to="/blog">
                        {text.buttons.allBlogs}
                    </Link>
                ) : (
                    <Link className="underline-hover" to={`/blog/${user.id}`}>
                        {text.buttons.yourBlog}
                    </Link>
                )}
            </div>
        </div>
    );

    return (
        <div id="blog-page">
            <PageHeader title={text.title} overrideProfile={profile} extra={addNewButton} />
            <section className="content mtm10" data-testid="blog-content">
                <SortFilter toggle={toggle} filterFields={filterFields} sortButtons={sortButtons} />
                <div className="row">
                    {loading ? (
                        <Loading />
                    ) : posts.length ? (
                        <div ref={didMountRef}>
                            {posts.map((p, i) => {
                                const isNew = differenceInCalendarDays(new Date(), p.date) < 7;
                                const isLive = new Date(p.date) <= new Date() && p.published;
                                return (
                                    <div key={`P-${p.id}-${i}`} className="col-lg-3 col-md-6 col-xs-12 post-card-holder">
                                        {isNew && <div className="featured-banner">{text.new}</div>}
                                        {(user.administrator || p.owner_Id === user.id) && (
                                            <FloatingMenu>
                                                <li
                                                    onClick={() => {
                                                        setPost({ ...p });
                                                        setShowManagePost(true);
                                                    }}
                                                >
                                                    <i className="fa fa-pencil mr10" />
                                                    {text.buttons.edit}
                                                </li>
                                                <li
                                                    onClick={() => {
                                                        togglePost(p);
                                                    }}
                                                >
                                                    <i className="fa fa-power-off mr10" />
                                                    {!p.published ? text.buttons.publish : text.buttons.unpublish}
                                                </li>
                                                <li
                                                    onClick={() => {
                                                        setPost({ ...p });
                                                        setShowWarning(true);
                                                    }}
                                                >
                                                    <i className="fa fa-trash mr10" />
                                                    {text.buttons.delete}
                                                </li>
                                            </FloatingMenu>
                                        )}
                                        <div
                                            id={`box-${i}`}
                                            onClick={() => history.push(`/blog/post/${p.id}`)}
                                            className={`box box-blog group-4 ${isLive ? '' : 'box-danger'}`}
                                        >
                                            <div className="post-card pointer">
                                                <div className="image-holder">
                                                    <BlobImage
                                                        folder="blog_Image"
                                                        src={p.blog_Image}
                                                        partnerId={p.partner_Id}
                                                    />
                                                </div>
                                                <h2>
                                                    <button
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            history.push(`/blog/post/${p.id}`);
                                                        }}
                                                        className="btn-link"
                                                    >
                                                        {p.title}
                                                    </button>
                                                </h2>
                                                {!profileId && (
                                                    <div className="mb5 text-sm">
                                                        {text.by + ' '}
                                                        <Link
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                history.push(`/blog/${p.owner_Id}`);
                                                            }}
                                                            to={`/blog/${p.owner_Id}`}
                                                        >
                                                            {p.owner_Full_Name}
                                                        </Link>
                                                    </div>
                                                )}
                                                <div className="mb10 text-sm">{e4Format(p.date)}</div>
                                                {p.description && <div className="mb10">{p.description}</div>}
                                                <div className="mb5">
                                                    <span className="mr10">
                                                        <i className="fa fa-comments mr5" />
                                                        {p.comment_Count}
                                                    </span>
                                                    <i className="fa fa-thumbs-up mr5" />
                                                    {p.like_Count}
                                                </div>
                                                {!isLive && <span className="label label-danger">{text.notLive}</span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="col-md-12 text-lg">
                            {search ? (
                                <NoResults title={text.noResults.title} message={text.noResults.message} />
                            ) : profileId === user.id ? (
                                <div className="text-center">
                                    <NoResults title={text.noPost.title} message={text.noPost.message} />
                                    <button
                                        className="btn btn-success btn-lg mt20"
                                        id="add-new-post-button-lg"
                                        onClick={() => {
                                            setShowManagePost(true);
                                            setPost({});
                                        }}
                                    >
                                        {text.noPost.start}
                                    </button>
                                </div>
                            ) : (
                                <NoResults title={text.noPosts.title} message={text.noPosts.message} />
                            )}
                        </div>
                    )}
                </div>
                {posts.length > 0 && (
                    <div className="text-center mt10">
                        <Paging
                            goToPage={page => setPage(page)}
                            recordCount={recordCount}
                            pageSize={pageSize}
                            className="btn-primary"
                            page={page}
                        />
                    </div>
                )}
            </section>
            {showManagePost && post && (
                <ManagePost
                    show={showManagePost}
                    close={() => setShowManagePost(false)}
                    reload={() => loadPosts()}
                    post={post}
                />
            )}
            <DeleteWarning
                message={text.warning}
                show={showWarning}
                close={confirm => {
                    deletePost(confirm);
                }}
            />
        </div>
    );
};

export default withRouter(Posts);
